import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {CopyableItemStyles, InitItemOpts, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {getBoldStrokeWidth, validateText} from '@PosterWhiteboard/libraries/text.library';
import type {Point} from '@Utils/math.util';
import {degreesToRadians} from '@Utils/math.util';
import type {RGBA} from '@Utils/color.util';
import {convertFabricColorToFillObject, rgbaToHex, rgbaToHexString, rgbaToRgbaString} from '@Utils/color.util';
import {noop} from '@Utils/general.util';
import {convertFillObjectToFabricColor} from '@Utils/fabric-fill.util';
import {toWellFormed} from '@Utils/string.util';
import {LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import {cloneLineStylesRecord, TextStyleRuns} from '@PosterWhiteboard/items/text-item/text-style-runs.class';
import type {TextLineListObject, TextLineStylesObject, TextRunStylesObject, TextStyleRunObject} from '@PosterWhiteboard/items/text-item/text-style-runs.types';
import {getUniformLineList} from '@PosterWhiteboard/items/text-item/text-line-list.library';
import {FONT_SIZE_MAX, FONT_SIZE_MIN} from '@PosterWhiteboard/items/text-item/text-item.types';
import {getRichTextPdfRasterizeReason} from '@PosterWhiteboard/items/text-item/text-item-pdf-safety.library';
import type {TextPdfRasterizeReason} from '@PosterWhiteboard/items/text-item/text-item-pdf-safety.library';
import {applyAlignmentToTextboxSelection, applyLineStylesToFabricTextbox, applyListToTextboxSelection, applyPerGraphemeRunStylesToTextboxSelection, applyRunStylesToTextboxSelection, applyStyleRunsToFabricTextbox, ensureSelectionApplyFontsAreLoaded, getEffectiveFontSizesForSelection, getGraphemeCount, getLineStylesClampedToTextbox, getTextSelectionStylesSummary, harvestLineStylesFromFabricTextbox, harvestStyleRunsFromFabricTextbox, refreshLineListMarkersOnTextbox, remeasureTextboxForLoadedFonts, replaceTextboxTextPreservingStyles, withExistingIndent} from '@PosterWhiteboard/items/text-item/text-style-runs-compiler';
import {DebouncedTextCommit, TEXT_COMMIT_DEBOUNCE_MS} from '@PosterWhiteboard/items/text-item/debounced-text-commit';
import {insertPastedTextIntoTextbox, resolvePastedText} from '@PosterWhiteboard/items/text-item/pasted-text.library';
import {TypedListPrefixConversion} from '@PosterWhiteboard/items/text-item/typed-list-prefix.library';
import {PendingTypingStyles} from '@PosterWhiteboard/items/text-item/pending-typing-styles.class';
import cloneDeep from 'lodash/cloneDeep';
import {getIsSpellCheckEnabledFromStore} from '@Libraries/spell-check-library';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {TextStylesObject} from '@PosterWhiteboard/classes/text-styles.class';
import {
  DEFAULT_STROKE_WIDTH,
  TEXT_OUTLINE_STROKE_WIDTH_FACTOR,
  TextHorizontalAlignType,
  TextStyles,
  TextVerticalAlignType,
} from '@PosterWhiteboard/classes/text-styles.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {closeTextSelectionPopup, handleTextSelectionPopupMenu, shouldShowTextSelectionPopup} from '@Libraries/text-selection-library';
import {fontSupportsText} from '@Utils/font.util';
import {ElementDataType} from '@Libraries/add-media-library';
import type {ItemCoordinates} from '@PosterWhiteboard/page/add-item.class';
import type {FabricObject, ObjectEvents, Point as FabricPoint, SerializedShadowOptions, TFiller, TPointerEvent} from '@postermywall/fabricjs-2';
import {Canvas, FixedLayout, Group, LayoutManager, Rect, Textbox, util} from '@postermywall/fabricjs-2';
import {TextBackground} from '@PosterWhiteboard/items/text-item/text-background';
import type {DeepPartial} from '@/global';
import {placeEditSessionCaret} from '@PosterWhiteboard/items/text-item/text-edit-caret.library';
import type {ClonePositionalParams, EnterEditModeOpts} from '@PosterWhiteboard/items/text-item/text-item.types';
import {addFonts, BULLETS_FONT_FAMILY, getFontFamilyNameForVariations, isBoldVariationAvaliableForFont, isFontLoaded, isInternalFont, loadBulletsFont} from '@/libraries/font-library';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {NUM_FRACTION_DIGITS} from '@PosterWhiteboard/common.types';
import round from 'lodash/round';
import {hideCustomControls, unhideCustomControls} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import {BorderType} from '@PosterWhiteboard/classes/item-border.class';
import {getTextBoxBorderWidth} from '@PosterWhiteboard/items/text-item/text-box-border.library';

import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {isTextEntryElementFocused} from '@Utils/dom.util';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

const DEFAULT_FONT_SIZE_FRACTION = 0.18;
export const DEFAULT_CACHE_EXPANSION_FACTOR = 1;
export const CUSTOM_FONT_CACHE_EXPANSION_FACTOR = 1.8;
export const MAX_CHARACTERS_LIMIT = '10000';
const PDF_UNREGISTRABLE_FONT_FAMILIES = ['SANTORegular', 'BarrioRegular'];
const PDF_UNSAFE_RTL_SCRIPTS = ['arabic', 'hebrew'];

export interface FabricItemDimensions {
  width: number;
  height: number;
}

interface ModelCoordinates {
  x?: number;
  y?: number;
}


/**
 * A width large enough that the fabric textbox never soft-wraps while measuring the natural
 * (unwrapped) width of the text. Used by {@link TextItem.updateTextWithoutWrapping}.
 */
const UNWRAPPED_TEXT_MEASURE_WIDTH = 100000;

/**
 * Extra slack (as a fraction of font size) added to the measured text width when fitting the box
 * to unwrapped text. Fabric's line-wrapping comparison accumulates width slightly differently than
 * {@link Textbox.calcTextWidth}, so an exact fit can tip the in-progress word onto a new line until
 * the next character (e.g. a space) is typed. The buffer keeps a word from wrapping while it is
 * still being typed.
 */
const UNWRAPPED_TEXT_WIDTH_BUFFER_FACTOR = 0.5;

/**
 * saves the list style from last pasted text
 */

export class TextItem extends Item {
  declare _fabricObject: Group;

  public gitype = ITEM_TYPE.TEXT;
  public text = '';
  public verticalAlign: TextVerticalAlignType = TextVerticalAlignType.TOP;
  public verticalPadding = 0;
  /**
   * Per-side inset between the text and the item's bounds, in the item's local space — it scales
   * with the item exactly like the text does (no /scale compensation). Legacy posters are migrated
   * from the old constant on-canvas 11px inset by back-computing `11 / scale` (MigrateToVersion68).
   */
  public padding = 0;
  public background: TextBackground;
  public backgroundFabricObject!: Rect;
  public fabricTextbox!: Textbox;
  public textStyles: TextStyles;
  public styleRuns = new TextStyleRuns();
  public lineStyles: Record<string, TextLineStylesObject> = {};
  public enterEditModeOnMouseUp = true;
  private hiddenTextareaPasteListener: ((event: Event) => void) | null = null;
  private hiddenTextareaInputListener: (() => void) | null = null;
  private hiddenTextareaKeydownListener: ((event: KeyboardEvent) => void) | null = null;
  private hiddenTextareaBeforeInputListener: ((event: Event) => void) | null = null;

  /**
   * Effective per-grapheme font sizes captured when a relative size drag starts, so every tick
   * scales from the original sizes instead of compounding off the previous tick. Cleared on the
   * drag's undoable end-commit.
   */
  /*
   * The private fields below are soft-`private`, not `#`: duplicating an item runs the live
   * instance through lodash cloneDeep (active-selection.class duplicateSelection), which rebuilds
   * from the prototype without running the constructor — a clone therefore has NO `#` slots, and
   * any later `#` read on it throws a brand-check TypeError (the storedRuns lesson in
   * text-style-runs.class.ts). `#` stays for private METHODS (they live on the prototype).
   */
  private selectionFontSizeScaleBaseline: {selectionStart: number; selectionEnd: number; effectiveFontSizes: number[]} | null = null;
  /**
   * Holds a base width assigned before the fabric textbox exists (item init / load). Once the
   * textbox is created, {@link baseWidth} reads and writes the textbox itself — fabric is the
   * single source of truth — and this value is only consulted by the legacy-load fallback in
   * {@link updateBaseWidth}.
   */
  private baseWidthBeforeFabricInit = 0;

  /**
   * The wrap width at edit-session start. Fabric grows the edited textbox's width when a long
   * word exceeds it; resetting to this width on every change lets the box shrink back once that
   * word is deleted, exactly like the pre-session width behaves between sessions.
   */
  private editSessionBaseWidth: number | null = null;
  private editSessionCacheRefreshDisposer: (() => void) | null = null;
  /** Disposers for the fabric listeners attached to the textbox for one edit session. */
  private editSessionListenerDisposers: VoidFunction[] = [];

  private readonly textCommit = new DebouncedTextCommit();

  /** Styles buffered at a collapsed caret for the next characters typed — see the class doc. */
  private readonly pendingTypingStyles = new PendingTypingStyles();
  /** Converts a typed "- ", "* " or "1. " into a list row — see the class doc of the conversion. */
  private readonly typedListPrefixConversion = new TypedListPrefixConversion();

  public get baseWidth(): number {
    return this.fabricTextbox ? this.fabricTextbox.width : this.baseWidthBeforeFabricInit;
  }

  /**
   * Writes the textbox width without fabric's layout: a write arrives with the model's new text
   * still to come (copyVals runs before the textbox is laid out), and laying out now would size
   * the box for the OLD text — undoing a typed long word restored the old baseWidth, fabric
   * re-grew it for the word still on the textbox, and the short text then kept that width. The
   * layout that follows every update sets text and width together.
   */
  public set baseWidth(width: number) {
    if (this.fabricTextbox) {
      this.fabricTextbox.width = width;
    } else {
      this.baseWidthBeforeFabricInit = width;
    }
  }

  /**
   * The textbox currently in fabric's text-editing mode, or null outside an edit session.
   * Editing happens in place (fabric edits inside interactive groups), so while editing this is
   * always {@link fabricTextbox}.
   */
  public getEditingTextbox(): Textbox | null {
    return this.fabricTextbox?.isEditing ? this.fabricTextbox : null;
  }

  constructor(page: Page) {
    super(page);
    this.textStyles = new TextStyles();
    this.background = new TextBackground(this);
  }

  override get fabricObject(): Group {
    return this._fabricObject;
  }

  public override async updateFromObject(
    textItemObject: DeepPartial<TextItemObject>,
    {
      updateRedux = true,
      applyLoadTimeNormalizations = false,
      undoable = true,
      fireModifiedEvent = true,
      doInvalidate = true,
      replayPosterOnUpdateDone = false,
      checkForDurationUpdate = false,
    }: UpdateFromObjectOpts = {}
  ): Promise<void> {
    // Commit any debounced typing first so this update applies on top of the edited textbox's
    // current text instead of overwriting it with a stale model (and so the pending timer can
    // never fire later and clobber this update).
    await this.flushPendingTextCommit();
    // Captured before the update applies — see Item.updateFromObject.
    const pageWasVideoBeforeUpdate = checkForDurationUpdate ? this.page.isVideo() : true;
    const isInTextEditMode = !!this.getEditingTextbox();
    this.copyVals(textItemObject);
    await this.updateFabricObjectFromObject(textItemObject);
    const fontsStillLoading = this.getFonts(true).filter((fontFamily) => !isFontLoaded(fontFamily));
    await this.ensureFontsAreLoaded();
    this.#remeasureForLoadedFonts(fontsStillLoading);

    if (applyLoadTimeNormalizations) {
      this.text = validateText(this.text);
      if (this.#lacksStoredBaseWidth(textItemObject)) {
        this.updateBaseWidth();
      }
    }

    if (doInvalidate) {
      await this.invalidate();
    }

    // Mirrors Item.updateFromObject: a span change without a clock tick must re-gate visibility.
    this.updateItemVisibility();

    if (checkForDurationUpdate) {
      this.checkItemForPageDurationUpdate(pageWasVideoBeforeUpdate);
    }

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.fire('modified');
    }
    // Restore typing focus to the canvas after click-type contextual options (e.g. Bold), but
    // never steal it from a field the user is typing in (e.g. the font size input), and only on
    // undoable commits — refocusing per slider tick (undoable: false) flickers the caret.
    const editingTextbox = this.getEditingTextbox();
    if (isInTextEditMode && undoable && editingTextbox && editingTextbox.hiddenTextarea !== document.activeElement && !isTextEntryElementFocused() && !isEditorMobileVariant()) {
      editingTextbox.hiddenTextarea?.focus({preventScroll: true});
    }
    if (editingTextbox) {
      this.#publishTextSelectionStyles();
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
    if (replayPosterOnUpdateDone) {
      await this.page.poster.replayPage(this.page.hashedID);
    }
  }

  public override getCopyableStyles(): CopyableItemStyles {
    const styles: CopyableItemStyles = {
      ...super.getCopyableStyles(),
      scaleX: this.fabricObject.scaleX,
      scaleY: this.fabricObject.scaleY,
      textStyles: this.textStyles.toObject(),
      frontFill: convertFillObjectToFabricColor(this.textStyles.fill.toObject()),
      backFill: this.background.fill.toObject(),
      verticalAlign: this.verticalAlign,
      verticalPadding: this.verticalPadding,
      styleRuns: this.styleRuns.toObject(),
      lineStyles: cloneDeep(this.lineStyles),
      textBorder: {
        solidBorderColor: this.textStyles.strokeColor,
        solidBorderType: this.textStyles.stroke ? BorderType.RECTANGLE_BORDER : BorderType.NONE,
        solidBorderThickness: this.textStyles.strokeWidth * 100,
      },
    };

    if (this.background.fill.fillType !== FillTypes.NONE) {
      styles.rx = this.background.rx;
      styles.ry = this.background.ry;
    }

    return styles;
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<TextItemObject> = {};
    if (copiedStyles.textStyles !== undefined) {
      stylesObj.textStyles = copiedStyles.textStyles;
    }

    if (copiedStyles.backFill !== undefined) {
      stylesObj.background = {
        fill: copiedStyles.backFill,
      };
    }
    if (copiedStyles.verticalAlign !== undefined) {
      stylesObj.verticalAlign = copiedStyles.verticalAlign;
    }
    if (copiedStyles.verticalPadding !== undefined) {
      stylesObj.verticalPadding = copiedStyles.verticalPadding;
    }
    if (copiedStyles.styleRuns !== undefined) {
      stylesObj.styleRuns = this.#getStyleRunsClampedToOwnText(copiedStyles.styleRuns);
    }
    if (copiedStyles.lineStyles !== undefined) {
      stylesObj.lineStyles = getLineStylesClampedToTextbox(copiedStyles.lineStyles, this.fabricTextbox);
    }
    if (copiedStyles.rx !== undefined) {
      stylesObj.background = {
        ...stylesObj.background,
        rx: copiedStyles.rx,
        ry: copiedStyles.ry,
      };
    }

    if (copiedStyles.textBorder !== undefined) {
      stylesObj.textStyles = {
        ...stylesObj.textStyles,
        stroke: copiedStyles.textBorder.solidBorderType !== BorderType.NONE,
        strokeWidth: copiedStyles.textBorder.solidBorderThickness / 100,
        strokeColor: copiedStyles.textBorder.solidBorderColor,
      };
    }

    if (copiedStyles.frontFill !== undefined) {
      stylesObj.textStyles = {
        ...stylesObj.textStyles,
        fill: convertFabricColorToFillObject(copiedStyles.frontFill),
      };
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['border']);
    await this.applyPastedStyles({...commonStylesObj, ...stylesObj});
  }

  public override copyVals(obj: DeepPartial<TextItemObject>): void {
    const {textStyles, text, background, styleRuns, lineStyles, ...itemObj} = obj;
    super.copyVals(itemObj);
    if (text !== undefined) {
      this.text = toWellFormed(text);
    }
    if (background) {
      this.background.copyVals(background);
    }
    if (styleRuns !== undefined) {
      this.styleRuns.replaceRuns(styleRuns);
    }
    if (lineStyles !== undefined) {
      this.lineStyles = cloneLineStylesRecord(lineStyles);
    }
    if (textStyles !== undefined && styleRuns === undefined && this.styleRuns.hasRuns()) {
      this.#reconcileRunsWithBaseChange(textStyles);
    }
    if (textStyles?.textAlign !== undefined && lineStyles === undefined && Object.keys(this.lineStyles).length > 0) {
      this.#clearLineAlignmentOverrides();
    }
    if (itemObj.shadow !== undefined && styleRuns === undefined && this.styleRuns.hasRuns()) {
      this.#clearRunShadowOverrides();
    }
    this.textStyles.copyVals(textStyles);
  }

  /**
   * A whole-box shadow write (set or remove) is an explicit "shadow it all": the item shadow
   * composites over the whole group, so surviving per-run shadow overrides could neither show
   * nor be cleared — they go with it.
   */
  #clearRunShadowOverrides(): void {
    const clearedRuns = this.styleRuns.toObject().map((run) => ({...run, styles: {...run.styles, shadow: null}}));
    this.styleRuns.replaceRuns(clearedRuns);
  }

  /**
   * A whole-box alignment write is an explicit "align it all" — per-line overrides would keep it
   * from visibly applying, so they clear; other per-line keys survive. Entries left with nothing
   * but nulls drop entirely.
   */
  #clearLineAlignmentOverrides(): void {
    const clearedLineStyles: Record<string, TextLineStylesObject> = {};
    for (const [lineIndex, lineStyleObject] of Object.entries(this.lineStyles)) {
      const cleared: TextLineStylesObject = {...lineStyleObject, textAlign: null};
      if (Object.values(cleared).some((value) => value !== null)) {
        clearedLineStyles[lineIndex] = cleared;
      }
    }
    this.lineStyles = clearedLineStyles;
  }

  /**
   * Keeps whole-box writes meaningful over mixed (run-styled) content: a whole-box font size
   * scales every run's size proportionally so the contrast between runs survives; any other
   * whole-box write becomes the new base AND clears that property's run overrides — a whole-box
   * write is an explicit "make it all X", so it clears even when the value already equals the
   * base. The one recurring equal-value echo (the debounced text commit's script/font detection)
   * is filtered out at its source in {@link updateText}.
   */
  #reconcileRunsWithBaseChange(incomingTextStyles: DeepPartial<TextStylesObject>): void {
    const scaleRunFontSizesBy = incomingTextStyles.fontSize !== undefined && incomingTextStyles.fontSize !== this.textStyles.fontSize && this.textStyles.fontSize > 0 ? incomingTextStyles.fontSize / this.textStyles.fontSize : null;
    const clearFontFamily = incomingTextStyles.fontFamily !== undefined;
    const clearIsBold = incomingTextStyles.isBold !== undefined;
    const clearIsItalic = incomingTextStyles.isItalic !== undefined;
    const clearUnderLine = incomingTextStyles.underLine !== undefined;
    const clearLineThrough = incomingTextStyles.lineThrough !== undefined;
    const clearFillColor = incomingTextStyles.fill !== undefined;
    const clearOutline = incomingTextStyles.stroke !== undefined || incomingTextStyles.strokeColor !== undefined || incomingTextStyles.strokeWidth !== undefined;

    const reconciledRuns = this.styleRuns.toObject().map((run) => ({
      ...run,
      styles: {
        ...run.styles,
        fontSize: scaleRunFontSizesBy !== null && run.styles.fontSize !== null ? run.styles.fontSize * scaleRunFontSizesBy : run.styles.fontSize,
        fontFamily: clearFontFamily ? null : run.styles.fontFamily,
        fontLicense: clearFontFamily ? null : run.styles.fontLicense,
        isBold: clearIsBold ? null : run.styles.isBold,
        isItalic: clearIsItalic ? null : run.styles.isItalic,
        underLine: clearUnderLine ? null : run.styles.underLine,
        lineThrough: clearLineThrough ? null : run.styles.lineThrough,
        fillColor: clearFillColor ? null : run.styles.fillColor,
        fillGradient: clearFillColor ? null : run.styles.fillGradient,
        outline: clearOutline ? null : run.styles.outline,
      },
    }));
    this.styleRuns.replaceRuns(reconciledRuns);
  }

  private async ensureFontsAreLoaded(): Promise<void> {
    await this.checkForFontsLoad();
    await this.checkForBulletsFontLoad();
  }

  /**
   * Pasted styles keep the source item's run indices; anything beyond this item's own text is
   * trimmed away.
   */
  #getStyleRunsClampedToOwnText(styleRuns: TextStyleRunObject[]): TextStyleRunObject[] {
    const clampedRuns = new TextStyleRuns();
    clampedRuns.replaceRuns(styleRuns);
    clampedRuns.clampToGraphemeCount(getGraphemeCount(this.fabricTextbox));
    return clampedRuns.toObject();
  }

  /**
   * Whether this load carries no usable baseWidth, so it must be derived from the stored box width.
   * Checks the INCOMING object, not {@link baseWidth}: once the textbox exists, baseWidth reads the
   * live textbox, and copying a stored 0 into it makes fabric grow the box to its widest word — a
   * non-zero width that would otherwise hide the missing value (items saved before baseWidth
   * existed store 0; ~0.8% of sampled production posters).
   */
  #lacksStoredBaseWidth(textItemObject: DeepPartial<TextItemObject>): boolean {
    if (textItemObject.baseWidth !== undefined) {
      return !(textItemObject.baseWidth > 0);
    }
    return this.baseWidthBeforeFabricInit === 0;
  }

  private updateBaseWidth(): void {
    if (this.fabricObject.width === 0) {
      this.baseWidth = this.textStyles.fontSize * (this.text.length / 2);
      this.fabricObject.width = this.baseWidth + this.padding * 2 + DEFAULT_STROKE_WIDTH;
    } else {
      this.baseWidth = this.calculateBaseWidthFromWidth();
    }
  }

  private calculateBaseWidthFromWidth(): number {
    if (this.fabricObject.width) {
      return this.fabricObject.width - this.padding * 2 - this.getStrokeWidth();
    }

    return 0;
  }

  public isTextEmpty(): boolean {
    return this.text === '';
  }

  public override getFonts(withVariation: boolean): string[] {
    const fonts = [withVariation ? getFontFamilyNameForVariations(this.textStyles.fontFamily, this.textStyles.isBold, this.textStyles.isItalic) : this.textStyles.fontFamily];
    for (const run of this.styleRuns.runs) {
      if (run.styles.fontFamily === null && run.styles.isBold === null && run.styles.isItalic === null) {
        continue;
      }
      const fontFamily = run.styles.fontFamily ?? this.textStyles.fontFamily;
      const font = withVariation ? getFontFamilyNameForVariations(fontFamily, run.styles.isBold ?? this.textStyles.isBold, run.styles.isItalic ?? this.textStyles.isItalic) : fontFamily;
      if (!fonts.includes(font)) {
        fonts.push(font);
      }
    }
    return fonts;
  }

  protected async hasUnsupportedText(): Promise<boolean> {
    const supported = this.getFonts(true).map((family) => {
      return fontSupportsText(family, this.text);
    });

    const hasSupportedTextArray = await Promise.all(supported);
    return hasSupportedTextArray.some((isSupported) => {
      return !isSupported;
    });
  }

  protected override async updateFabricObjectFromObject(obj: DeepPartial<TextItemObject>): Promise<void> {
    await super.updateFabricObjectFromObject(obj);
    // this.fabricObject.set('shadow', null);
    //
    // if (obj.shadow !== undefined) {
    //   if (obj.shadow === null) {
    //     this.fabricTextbox.set('shadow', null);
    //   } else {
    //     const unserializedObj = await util.enlivenObjectEnlivables({
    //       shadow: {
    //         ...this.fabricObject.shadow,
    //         ...obj.shadow,
    //       },
    //     });
    //
    //     this.fabricTextbox.set('shadow', unserializedObj.shadow);
    //   }
    // }
  }

  public hasBackground(): boolean {
    return this.background.fill.hasFill();
  }

  public override async isPDFSafe(): Promise<boolean> {
    if (this.getRichTextPdfRasterizeReason() !== null) {
      return false;
    }

    // These fonts fail to register in pdf. Investigate later why
    if (this.getFonts(false).some((fontFamily) => PDF_UNREGISTRABLE_FONT_FAMILIES.includes(fontFamily))) {
      return false;
    }

    if (this.fabricObject.shadow) {
      return false;
    }

    try {
      const incompatibleFont = await this.hasUnsupportedText();
      const incompatibleDecorations =
        (this.textStyles.letterSpacing > 0 && (this.textStyles.underLine || this.textStyles.lineThrough)) || // fabricJS breaks decoration on individual characters
        (this.textStyles.lineThrough && this.textStyles.underLine) ||
        this.background.fill.fillType === FillTypes.RADIAL_GRADIENT ||
        this.textStyles.stroke; // bug in svg-to-pdfkit does not treat decorations as a list, so need to rasterize if using both together

      return !PDF_UNSAFE_RTL_SCRIPTS.includes(this.textStyles.script) && !incompatibleFont && !incompatibleDecorations;
    } catch {
      return false;
    }
  }

  /** Style runs and per-line alignment export as vector text; lists, gradient runs, outlined, shadowed or decorated runs do not. */
  public getRichTextPdfRasterizeReason(): TextPdfRasterizeReason | null {
    return getRichTextPdfRasterizeReason({styleRuns: this.styleRuns.toObject(), lineStyles: this.lineStyles, baseStyles: this.textStyles});
  }

  protected hasCenterHorizontalAlignment(): boolean {
    return this.textStyles.textAlign === TextHorizontalAlignType.CENTER;
  }

  protected override isRoundedBorderSupported(): boolean {
    return false;
  }

  protected hasRightHorizontalAlignment(): boolean {
    return this.textStyles.textAlign === TextHorizontalAlignType.RIGHT;
  }

  public override toObject(): TextItemObject {
    return {
      ...super.toObject(),
      shadow: this.fabricObject.shadow ? (this.fabricObject.shadow.toObject() as SerializedShadowOptions) : null,
      // shadow: null,
      textStyles: this.textStyles.toObject(),
      baseWidth: this.baseWidth,
      fontFamily: this.textStyles.fontFamily,
      verticalAlign: this.verticalAlign,
      verticalPadding: round(this.verticalPadding, NUM_FRACTION_DIGITS),
      padding: this.padding,
      background: this.background.toObject(),
      text: toWellFormed(this.text),
      styleRuns: this.styleRuns.toObject(),
      lineStyles: cloneDeep(this.lineStyles),
    };
  }

  protected override onBeforeItemInitialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      addFonts([this.textStyles.getFontFamilyToLoad()], resolve, reject);
    });
  }

  /**
   * The box border is the item's base `border`, painted on the background rect instead of the
   * group: the base class's group stroke never applies to a text item (a group paints no stroke
   * and the stroke width would pad its box), the way the QR item paints its own container border.
   */
  protected override applyBorder(): void {
    this.setBackgroundParams();
  }

  public setBackgroundParams(): void {
    // The border's width is in canvas pixels and does not scale with the item, like every other
    // item border. Fabric's strokeUniform cannot do that here: it draws a child divided by the
    // group's total scaling but sizes its box by the child's own scale, so inside a scaled group
    // the rect overflows the box. The stroke is a plain local stroke of width ÷ scale instead,
    // recomputed on every scale step. It strokes INSIDE the group bounds — fabric's left/top
    // already position the stroke-inclusive box, so only the path shrinks by the stroke width —
    // and the group's dimensions and object cache never grow.
    const borderWidth = getTextBoxBorderWidth(this.border);
    const borderWidthInLocalUnits = borderWidth / this.#getEffectiveScaleForBorder();
    this.backgroundFabricObject.set({
      fill: this.background.fill.getFill(this.fabricObject.width, this.fabricObject.height),
      left: -this.fabricObject.width / 2,
      top: -this.fabricObject.height / 2,
      width: this.fabricObject.width - borderWidthInLocalUnits,
      height: this.fabricObject.height - borderWidthInLocalUnits,
      rx: this.background.rx,
      ry: this.background.ry,
      stroke: borderWidth > 0 ? rgbaToRgbaString(this.border.solidBorderColor) : null,
      strokeWidth: borderWidthInLocalUnits,
    });
  }

  /**
   * The scale the border's canvas-pixel width is divided by to get its local stroke width: the
   * group's total scaling, which for a text slide includes the slideshow it sits in.
   */
  #getEffectiveScaleForBorder(): number {
    const scale = this.fabricObject.getObjectScaling().x;
    return scale > 0 ? scale : 1;
  }

  public override async beforeInitFabricObject(): Promise<void> {
    await super.beforeInitFabricObject();
    await this.checkForBulletsFontLoad();
  }

  public getBaseWidth(): number {
    return this.fabricObject.width - this.padding * 2 - this.getStrokeWidth();
  }

  public override onScaling(): void {
    this.updateFabricGroupHeight();
    this.updateFabricGroupWidth();
    this.setBackgroundParams();
    this.setTextChildPosition();

    super.onScaling();
  }

  protected override initEvents(): void {
    super.initEvents();
    this.fabricObject.on('mousedown:before', this.onMouseDownBefore.bind(this));
    this.fabricObject.on('mouseup', this.onMouseUp.bind(this));
    this.fabricObject.on('mouseup:before', this.onMouseUpBefore.bind(this));

    // TODO: Do this when working on spell check.
    this.fabricObject.on('deselected', this.onDeselected.bind(this));
  }

  protected onDeselected(): void {
    // TODO: When generation/view page flags are set
    // if (!postermywall.Core.isPosterGenerating && !postermywall.Core.isViewPage) {
    this.page.spellCheck.clearSquigglyLineStyle(this.fabricTextbox);
    // }
  }

  protected async getFabricObjectForItem(opts: InitItemOpts = {}): Promise<Group> {
    if (!this.baseWidth && opts.width !== undefined) {
      this.baseWidth = opts.width - this.padding * 2 - this.getStrokeWidth();
    }

    const {shadow} = await util.enlivenObjectEnlivables({
      shadow: opts?.shadow ?? null,
    });

    this.fabricTextbox = new Textbox(this.text, {
      ...this.textStyles.getTextStyles(this.baseWidth, 100),
      statefullCache: true,
      width: this.baseWidth,
      perPixelTargetFind: true,
      // The textbox becomes the canvas' active object during in-place editing; these flags keep
      // it type-only there (no dragging/scaling, no control handles, just the editing frame).
      hasControls: false,
      hasBorders: true,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      // @ts-expect-error TODO: Remove this later.
      _fontSizeFraction: FONTSIZE_FRACTION[this.textStyles.fontFamily] ?? DEFAULT_FONT_SIZE_FRACTION,
    });

    this.backgroundFabricObject = new Rect({
      width: opts?.width ?? this.fabricTextbox.width,
      height: opts?.height ?? this.fabricTextbox.height,
      fill: this.background.fill.getFill(opts?.width ?? this.fabricTextbox.width, opts?.height ?? this.fabricTextbox.height),
      strokeWidth: 0,
      uniformScaling: false,
      uniformRoundness: true,
    });

    const fabricObject = new Group([this.backgroundFabricObject, this.fabricTextbox], {
      ...super.getCommonOptions(),
      width: this.calculateGroupWidth(),
      height: this.calculateGroupHeight(),
      useSelectedFlag: true,
      selected: false,
      caterCacheForTextChildren: true,
      perPixelTargetFind: true,
      layoutManager: new LayoutManager(new FixedLayout()),
      scaleX: opts?.scaleX ?? 1,
      scaleY: opts?.scaleY ?? 1,
    });

    fabricObject.set('shadow', shadow);

    return fabricObject;
  }

  public override async updateFabricObject(): Promise<void> {
    // `baseWidth` reads the live textbox width, and the option pass below lays the textbox out
    // with its OLD text styles still applied: a write that shrinks the width and the letter
    // spacing together (undo of a spacing that grew the box) re-grew the box in that pass, and
    // reading the width afterwards kept it grown (reported 2026-10-12). Capture the written width first.
    const baseWidth = this.baseWidth;
    await super.updateFabricObject();
    this.fabricTextbox.set(this.getOptionsForTextItem());
    this.fabricTextbox.set({
      ...this.textStyles.getTextStyles(this.fabricTextbox.width, this.fabricTextbox.height),
      // Not applying width here breaks undo for text item width not getting restored for font size that causes different text item width (rev. 62225) (has to do with the order in which styles are applied to fabric textbox).
      text: this.text,
      width: baseWidth,
    });
    // have to reapply text item fontStyle because it gives bad fabric Textbox height otherwise (Rev. 63548) (has to do with the order in which styles are applied to fabric textbox).
    this.fabricTextbox.set('fontStyle', this.textStyles.getFontStyle());

    if (this.fabricTextbox.hiddenTextarea) {
      this.fabricTextbox.hiddenTextarea.value = this.text;
    }

    this.applyTextStroke(this.fabricTextbox);
    applyStyleRunsToFabricTextbox(this.fabricTextbox, this.styleRuns.runs, this.textStyles);
    applyLineStylesToFabricTextbox(this.fabricTextbox, this.lineStyles);
    // Every layout above the run compile measured the OLD per-character styles still on fabric
    // (a run scaled up by a whole-box font size that is now being undone), and fabric never
    // shrinks a box it grew. Size the box for the NEW styles: the written width, grown only if
    // a word is wider than it.
    this.fabricTextbox.set('width', baseWidth);
    this.fabricTextbox.initDimensions();
    // The dimensions are set again in case sidebar-driven modifications (font size etc) changed the width.
    this.updateFabricGroupHeight();
    this.updateFabricGroupWidth();

    this.setBackgroundParams();
    this.setTextChildPosition();
    this.fabricTextbox.setCoords();
    if (this.getEditingTextbox()) {
      this.page.spellCheck.checkSpell(this.fabricTextbox, true).catch(noop);
    }
    this.fabricObject.setCoords();
  }

  public override getScaleForShadow(): number {
    return 1;
  }

  protected checkForFontsLoad(): Promise<void> {
    return new Promise((resolve, reject) => {
      // getFonts(true) is the base variation plus every style run's family×(bold,italic)
      // variation — run fonts must be loaded before the runs compile onto fabric.
      addFonts(this.getFonts(true), resolve, reject);
    });
  }

  protected async checkForBulletsFontLoad(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.hasUnorderedListLine()) {
        loadBulletsFont(BULLETS_FONT_FAMILY, () => {
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  public getActiveText(): string {
    return this.text;
  }

  protected getOptionsForTextItem(): Record<string, any> {
    return {
      text: this.text,
      // shadow: this.fabricObject.shadow,
      cursorWidth: 2,
      width: this.baseWidth,
      cursorColor: this.getCursorColor(),
      _fontSizeFraction:
        this.textStyles.fontFamily in FONTSIZE_FRACTION ? FONTSIZE_FRACTION[this.textStyles.fontFamily as keyof typeof FONTSIZE_FRACTION] : DEFAULT_FONT_SIZE_FRACTION,
      cacheExpansionFactor: this.getCacheExpansionFactor(),
    };
  }

  private getCacheExpansionFactor(): number {
    if (this.textStyles.fontFamily in CUSTOM_CACHE_EXPANSION_FACTOR) {
      return CUSTOM_CACHE_EXPANSION_FACTOR[this.textStyles.fontFamily as keyof typeof CUSTOM_CACHE_EXPANSION_FACTOR];
    }
    if (!isInternalFont(this.textStyles.fontFamily)) {
      return CUSTOM_FONT_CACHE_EXPANSION_FACTOR;
    }
    return DEFAULT_CACHE_EXPANSION_FACTOR;
  }

  protected override applyShadowToFabricObject(): void {
    this.fabricObject.set('shadow', this.fabricObject.shadow);
  }

  public getCursorColor(): string {
    return `#${rgbaToHex(this.textStyles.fill.fillColor[0])}`;
  }

  protected override onFabricObjectModified(): void {
    this.updateFromObject(
      {
        verticalPadding: this.getVerticalPadding(),
      },
      {
        undoable: false,
        updateRedux: false,
      }
    ).catch((e) => {
      console.error(`Failed to update item on fabric object modified, Details:${JSON.stringify(e)}`);
    });
  }

  public applyTextStroke(fabricTextboxItem: Textbox): void {
    if (this.textStyles.stroke) {
      fabricTextboxItem.set('strokeWidth', this.textStyles.fontSize * this.textStyles.strokeWidth * TEXT_OUTLINE_STROKE_WIDTH_FACTOR);
      fabricTextboxItem.set('strokeLineJoin', 'round');
      fabricTextboxItem.set('paintFirst', 'stroke');
      fabricTextboxItem.set('stroke', rgbaToHexString(this.textStyles.strokeColor));
    } else if (!this.textStyles.isBold) {
      fabricTextboxItem.set('strokeWidth', DEFAULT_STROKE_WIDTH);
      fabricTextboxItem.set('strokeLineJoin', 'miter');
      fabricTextboxItem.set('paintFirst', 'fill');
      fabricTextboxItem.set('stroke', undefined);
    }
  }

  protected onMouseDownBefore(): void {
    if (this.fabricObject === this.page.activeSelection.getActiveObject()) {
      this.beforeEnterEditMode();
    }
  }

  // TODO: change the type when migrated to fabric v6.
  private onMouseUp(options: ObjectEvents['mouseup']): void {
    // Don't enter the editing mode if the mouse up event happened because of an action performed by control button
    const mouseEvent = options.e as MouseEvent;
    if (!this.enterEditModeOnMouseUp || options.transform?.actionPerformed || (mouseEvent.button && mouseEvent.button !== 1)) {
      this.enterEditModeOnMouseUp = true;
      return;
    }

    this.onEnterEditMode(options.e);
  }

  private onMouseUpBefore(options: ObjectEvents['mouseup:before']): void {
    if (this.page.longPressInitiated || options.target?.__corner) {
      this.enterEditModeOnMouseUp = false;
    }
  }

  public enterEditMode(e?: MouseEvent, {selectAllText = false}: EnterEditModeOpts = {}): void {
    this.beforeEnterEditMode();
    this.onEnterEditMode(e, selectAllText);
  }

  protected beforeEnterEditMode(): void {
    this.fabricObject.selected = true;
  }

  public onEnterEditMode(e?: TPointerEvent, selectAllText = false): void {
    if (this.getEditingTextbox()) {
      // Already in edit mode. In-place editing means clicks on the text itself also bubble this
      // far; only the mobile "soft keyboard was dismissed while the hidden textarea kept focus"
      // case needs a focus toggle to re-summon the keyboard.
      if (isEditorMobileVariant() && this.page.canvasPanOnTextEdit.isKeyboardHidden()) {
        this.refocusHiddenTextarea();
      }
      return;
    }
    if (this.fabricObject.selected) {
      this.enterInPlaceEditMode(e);
      placeEditSessionCaret({textbox: this.fabricTextbox, event: e, canvas: this.page.fabricCanvas, selectAllText});
    }
  }

  /**
   * On mobile, tapping the text while already in edit mode only repositions the cursor; if the soft keyboard was
   * previously dismissed (e.g. via the device back button) the still-focused hidden textarea won't re-summon it.
   * Toggle focus so the keyboard re-opens, leaving the edit session and the current selection untouched.
   */
  private onEditingTextboxMouseUp(): void {
    if (!isEditorMobileVariant() || !this.getEditingTextbox() || !this.page.canvasPanOnTextEdit.isKeyboardHidden()) {
      return;
    }
    this.refocusHiddenTextarea();
  }

  /**
   * Forces a focus change on the edited textbox's hidden textarea so the mobile soft keyboard re-opens.
   * Fabric's `blur` handler only aborts the cursor animation (it does not exit editing), so the session and
   * its selection survive the toggle; the cursor animation is restarted afterwards.
   */
  private refocusHiddenTextarea(): void {
    const editingTextbox = this.getEditingTextbox();
    const hiddenTextarea = editingTextbox?.hiddenTextarea;
    if (!editingTextbox || !hiddenTextarea) {
      return;
    }
    hiddenTextarea.blur();
    hiddenTextarea.focus();
    editingTextbox.initDelayedCursor(true);
  }

  /**
   * Fabric paints the caret and the selection highlight on the top context from the textbox's
   * own render. Inside a cached group that render only runs when the cache is rebuilt, so any
   * render that clears the top context (hover borders, the grid, bleed and fold overlays mark it
   * dirty) wiped the highlight until the text changed — a double-clicked word looked unselected
   * while typing still replaced it. The group draws its children directly for the session.
   * The slideshow calls these for the edited text slide's own group, which caches inside the
   * (never cached) slideshow group.
   */
  /**
   * Fabric paints the caret and the selection highlight on the canvas's top context from the
   * textbox's render, which a cached group only runs while its cache is dirty. Any overlay that
   * clears the top context would then lose the highlight. The group keeps its cache for the
   * session — an uncached group renders every child with the group's shadow, so an item with a
   * box shadow would show a text shadow while editing — and is marked dirty before every render
   * instead, so the textbox renders each frame exactly as it does outside editing.
   */
  public keepGroupCacheFreshForEditSession(): void {
    this.stopKeepingGroupCacheFreshAfterEditSession();
    this.editSessionCacheRefreshDisposer = this.page.fabricCanvas.on('before:render', (): void => {
      this.fabricObject.dirty = true;
    });
  }

  public stopKeepingGroupCacheFreshAfterEditSession(): void {
    this.editSessionCacheRefreshDisposer?.();
    this.editSessionCacheRefreshDisposer = null;
  }

  private enterInPlaceEditMode(e?: TPointerEvent): void {
    if (isEditorMobileVariant()) {
      trackPosterBuilderGA4Events(GA4EventName.TEXT_EDIT, {[GA4EventParamName.TYPE]: 'OnCanvas'});
    }
    hideCustomControls(this.page.hashedID);
    const canvas = this.page.fabricCanvas;
    const textbox = this.fabricTextbox;
    textbox.__PMWID = this.uid;
    this.typedListPrefixConversion.startSession(textbox);
    this.editSessionBaseWidth = this.baseWidth;
    // Editing happens in place: the group routes pointer events to the textbox only while a
    // session is active, so item-level dragging/targeting is unaffected outside editing.
    // Per-pixel hit testing is disabled on the textbox for the session so clicks between glyphs
    // position the cursor instead of falling through to the group (which would exit editing).
    this.fabricObject.set({subTargetCheck: true, interactive: true});
    textbox.set({perPixelTargetFind: false});
    this.keepGroupCacheFreshForEditSession();
    if (canvas instanceof Canvas) {
      canvas.setActiveObject(textbox);
    }

    this.editSessionListenerDisposers.push(
      textbox.on('editing:entered', this.onTextEditModeEntered.bind(this)),
      textbox.on('editing:exited', this.onEditingExited.bind(this)),
      textbox.on('changed', () => {
        this.onCanvasTextChange(this.fabricTextbox.text);
      }),
      textbox.on('selection:changed', this.onSelectionChanged.bind(this)),
      textbox.on('mouseup', this.onEditingTextboxMouseUp.bind(this))
    );

    textbox.enterEditing(e);
    this.page.canvasPanOnTextEdit.beforeTextEditEntered();
    // Fabric creates a fresh hidden textarea per editing session and removes it from the DOM on
    // exit, so these listeners live and die with the session.
    const hiddenTextarea = textbox.hiddenTextarea;
    if (hiddenTextarea) {
      this.hiddenTextareaPasteListener = (event: Event): void => {
        void this.onPasteText(event);
      };
      this.hiddenTextareaInputListener = (): void => {
        this.#applyPendingTypingStylesToTypedCharacters();
        this.onTextInput();
      };
      this.hiddenTextareaKeydownListener = (event: KeyboardEvent): void => {
        this.handleEditModeStyleShortcut(event);
        this.#handleListEnterKey(event);
        this.#handleListBackspaceKey(event);
      };
      this.hiddenTextareaBeforeInputListener = (event: Event): void => {
        if (event instanceof InputEvent) {
          this.pendingTypingStyles.armOnBeforeInput(event);
        }
      };
      hiddenTextarea.addEventListener('paste', this.hiddenTextareaPasteListener);
      hiddenTextarea.addEventListener('beforeinput', this.hiddenTextareaBeforeInputListener);
      hiddenTextarea.addEventListener('input', this.hiddenTextareaInputListener);
      hiddenTextarea.addEventListener('keydown', this.hiddenTextareaKeydownListener);
    }
    this.#publishTextSelectionStyles();
  }

  private onTextInput(): void {
    setTimeout(() => {
      this.page.canvasPanOnTextEdit.scrollCanvasToText();
    }, 150);
  }

  /**
   * Buffers styles at the collapsed caret for the NEXT characters typed (rich-text pending
   * styles) — nothing on the canvas changes until typing consumes the buffer. The style surfaces
   * immediately display the buffered value via the published summary overlay.
   */
  public setPendingTypingStyles(stylesPartial: Partial<TextRunStylesObject>): void {
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox || editingTextbox.selectionStart !== editingTextbox.selectionEnd) {
      return;
    }
    this.pendingTypingStyles.mergeAtCaret(editingTextbox, stylesPartial);
    void this.#repairTypedTextWhenPendingFontsLoad(editingTextbox, stylesPartial);
    this.#publishTextSelectionStyles();
  }

  /**
   * Typing cannot wait for a font face, so characters typed with a pending family or variation
   * before its face arrives are measured in the fallback face. Once the face loads, drop those
   * widths and lay the box out again around the glyphs it really draws.
   */
  async #repairTypedTextWhenPendingFontsLoad(editingTextbox: Textbox, stylesPartial: Partial<TextRunStylesObject>): Promise<void> {
    const loadedFontFamilies = await ensureSelectionApplyFontsAreLoaded(editingTextbox, stylesPartial, this.textStyles).catch((): string[] => []);
    if (loadedFontFamilies.length === 0 || this.getEditingTextbox() !== editingTextbox) {
      return;
    }
    remeasureTextboxForLoadedFonts(editingTextbox, loadedFontFamilies);
    this.#fitBoxToEditingTextbox(editingTextbox);
    this.page.fabricCanvas.requestRenderAll();
  }

  #remeasureForLoadedFonts(loadedFontFamilies: string[]): void {
    if (loadedFontFamilies.length === 0 || !this.fabricTextbox) {
      return;
    }
    remeasureTextboxForLoadedFonts(this.fabricTextbox, loadedFontFamilies);
  }

  #applyPendingTypingStylesToTypedCharacters(): void {
    const editingTextbox = this.getEditingTextbox();
    if (editingTextbox && this.pendingTypingStyles.consumeOnInput(editingTextbox, this.textStyles)) {
      editingTextbox.dirty = true;
      this.page.fabricCanvas.requestRenderAll();
      this.#publishTextSelectionStyles();
    }
  }

  public onSelectionChanged(): void {
    const editingTextbox = this.getEditingTextbox();
    if (editingTextbox) {
      this.pendingTypingStyles.discardOnSelectionChange(editingTextbox);
    }
    if (shouldShowTextSelectionPopup(editingTextbox)) {
      handleTextSelectionPopupMenu(editingTextbox);
    } else if (getIsSpellCheckEnabledFromStore() && editingTextbox) {
      void this.page.spellCheck.handleSuggestionPopupMenu(editingTextbox);
    } else {
      closeTextSelectionPopup();
    }
    this.#publishTextSelectionStyles();
  }

  public async onPasteText(e?: Event): Promise<void> {
    this.pendingTypingStyles.discard();
    const pastedText = await resolvePastedText(e);
    const editingTextbox = this.getEditingTextbox();
    if (editingTextbox && pastedText) {
      insertPastedTextIntoTextbox(editingTextbox, pastedText);
      this.onCanvasTextChange(editingTextbox.text);
    }
  }

  public onCanvasTextChange(text = ''): void {
    this.textCommit.cancel();

    const textbox = this.getEditingTextbox();
    if (textbox) {
      const convertedText = this.typedListPrefixConversion.convert(textbox, this.lineStyles, (): void => {
        this.#replayTextChangeOnceBulletsFontLoaded(textbox);
      })
        ? textbox.text
        : text;
      refreshLineListMarkersOnTextbox(textbox);
      this.page.spellCheck.clearSquigglyLineAroundCaret(textbox);
      const {newModelCoords, newVerticalPadding} = this.#fitBoxToEditingTextbox(textbox);
      this.textCommit.schedule(() => this.updateText(convertedText, newModelCoords, newVerticalPadding), TEXT_COMMIT_DEBOUNCE_MS);
    }
  }

  /**
   * Re-lays the group out around the edited textbox's current dimensions: the textbox keeps at
   * least its session width (growing only for a word that cannot wrap), the group follows, and
   * the origin moves so the edge the vertical alignment pins stays put.
   */
  #fitBoxToEditingTextbox(textbox: Textbox): {newModelCoords: ModelCoordinates; newVerticalPadding: number} {
    const textboxDimensions = {
      width: textbox.width + textbox.strokeWidth,
      height: textbox.height + textbox.strokeWidth,
    };
    const heightWithoutVerticalPadding = textbox.height + textbox.strokeWidth + this.padding * 2;
    const newVerticalPadding = Math.min(Math.max(this.fabricObject.height - heightWithoutVerticalPadding, 0), this.verticalPadding);
    // caching these 2 variables before updating group dimensions as they are needed in the next step.
    const textboxCenterPoint = textbox.getCenterPoint();
    const groupBottomLeft = this.fabricObject.aCoords.bl;

    textbox.set({width: Math.max(textbox.dynamicMinWidth ?? 0, this.editSessionBaseWidth ?? this.baseWidth)});

    this.updateGroupDimensions(textboxDimensions, newVerticalPadding);

    const newModelCoords = this.getUpdatedModelCoords(textboxCenterPoint, groupBottomLeft);
    this.fabricObject.set({
      left: newModelCoords.x,
      top: newModelCoords.y,
    });
    this.fabricObject.setCoords();
    this.setTextChildPosition();
    this.setBackgroundParams();
    textbox.setCoords();
    return {newModelCoords, newVerticalPadding};
  }

  /**
   * Enter on an EMPTY list row exits the list (clears that row's list entry instead of adding a
   * row), matching standard editors.
   */
  #handleListEnterKey(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return;
    }
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox || editingTextbox.selectionStart !== editingTextbox.selectionEnd) {
      return;
    }
    const {lineIndex} = editingTextbox.get2DCursorLocation(editingTextbox.selectionStart, true);
    const lineText = (editingTextbox.text ?? '').split('\n')[lineIndex] ?? '';
    if (lineText !== '' || !harvestLineStylesFromFabricTextbox(editingTextbox)[lineIndex]?.list) {
      return;
    }
    event.preventDefault();
    void this.applyListToTextSelection(null);
  }

  /**
   * Backspace at the START of a list row removes the row's bullet (clears its list entry)
   * instead of merging into the previous line, matching standard editors.
   */
  #handleListBackspaceKey(event: KeyboardEvent): void {
    if (event.key !== 'Backspace' || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return;
    }
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox || editingTextbox.selectionStart !== editingTextbox.selectionEnd) {
      return;
    }
    const {lineIndex, charIndex} = editingTextbox.get2DCursorLocation(editingTextbox.selectionStart, true);
    if (charIndex !== 0 || !harvestLineStylesFromFabricTextbox(editingTextbox)[lineIndex]?.list) {
      return;
    }
    event.preventDefault();
    void this.applyListToTextSelection(null);
  }

  /** The bullets face arrived after a typed "- ": re-run the keystroke's text change, whose conversion is re-armed. */
  #replayTextChangeOnceBulletsFontLoaded(textbox: Textbox): void {
    if (this.getEditingTextbox() === textbox) {
      this.onCanvasTextChange(textbox.text ?? '');
    }
  }

  public updateText(
    text = '',
    modelNewCoordinates: ModelCoordinates = {
      x: this.fabricObject.getX(),
      y: this.fabricObject.getY(),
    },
    newVerticalPadding = this.verticalPadding
  ): Promise<void> {
    return this.updateFromObject({
      text,
      width: this.fabricObject.width,
      height: this.fabricObject.height,
      verticalPadding: newVerticalPadding,
      left: modelNewCoordinates.x,
      top: modelNewCoordinates.y,
      textStyles: this.#getScriptStylesForTextCommit(text),
      ...this.#getTextCommitStyleOverrides(),
    });
  }

  /**
   * The debounced commit's script/font detection, with an unchanged font family filtered out —
   * it is an echo of the current base, not a user write, and must not clear run font overrides
   * (see {@link #reconcileRunsWithBaseChange}).
   */
  #getScriptStylesForTextCommit(text: string): Partial<TextStylesObject> {
    const detectedScriptStyles = this.textStyles.getLanguageScriptAndFontForText(text);
    if (detectedScriptStyles.fontFamily === this.textStyles.fontFamily) {
      delete detectedScriptStyles.fontFamily;
    }
    return detectedScriptStyles;
  }

  /**
   * The run/line styles to commit alongside a text change. On-canvas edits mutate the edited
   * textbox's styles map, which fabric shifts wrap-aware on every insert/remove — harvesting it
   * is the single source of run indices. Outside an edit session the live textbox is equally
   * authoritative: wholesale replacements pre-shift it via
   * {@link TextItem.replaceWholeTextPreservingRuns} before committing.
   */
  #getTextCommitStyleOverrides(): DeepPartial<TextItemObject> {
    const textbox = this.getEditingTextbox() ?? this.fabricTextbox;
    return {styleRuns: harvestStyleRunsFromFabricTextbox(textbox), lineStyles: harvestLineStylesFromFabricTextbox(textbox)};
  }

  /**
   * Runs the debounced on-canvas text commit immediately. Called before anything reads or
   * overwrites the model mid-edit (external updates, editing exit), so the model and undo
   * history never see text older than the edited textbox's — and the cleared timer can never
   * fire later with stale text.
   */
  public flushPendingTextCommit(): Promise<void> {
    return this.textCommit.flush();
  }

  /**
   * Lands debounced typing in poster history before leaving edit mode, so poster undo/redo
   * cannot race a late text commit.
   */
  public async exitEditModeCommittingText(): Promise<void> {
    await this.flushPendingTextCommit();
    this.restoreDefaultState();
  }

  /**
   * Applies semantic style overrides to the current text-editing selection: the partial merges
   * into the selection's existing run passengers on the edited textbox, recompiles there, and
   * the harvested runs commit as ONE undoable update. No-ops without an edit session or with a
   * collapsed selection — callers fall back to a whole-item write for those.
   */
  public async applyStylesToTextSelection(stylesPartial: Partial<TextRunStylesObject>, {undoable = true}: UpdateFromObjectOpts = {}): Promise<void> {
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox || editingTextbox.selectionStart >= editingTextbox.selectionEnd) {
      return;
    }
    await this.flushPendingTextCommit();
    await ensureSelectionApplyFontsAreLoaded(editingTextbox, stylesPartial, this.textStyles);
    applyRunStylesToTextboxSelection(editingTextbox, stylesPartial, this.textStyles, editingTextbox.selectionStart, editingTextbox.selectionEnd);
    await this.updateFromObject({styleRuns: harvestStyleRunsFromFabricTextbox(editingTextbox)}, {undoable});
  }

  /**
   * Aligns every real line the current text-editing selection touches (its own line for a
   * collapsed caret — alignment is a line property, so unlike the run styles it needs no
   * non-collapsed selection). Committed through the model as ONE undoable update.
   */
  public async applyAlignmentToTextSelection(textAlign: TextHorizontalAlignType, {undoable = true}: UpdateFromObjectOpts = {}): Promise<void> {
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox) {
      return;
    }
    await this.flushPendingTextCommit();
    applyAlignmentToTextboxSelection(editingTextbox, textAlign, editingTextbox.selectionStart, editingTextbox.selectionEnd);
    await this.updateFromObject({lineStyles: harvestLineStylesFromFabricTextbox(editingTextbox)}, {undoable});
  }

  /**
   * Sets (or clears, list null) list membership on every real line the current text-editing
   * selection touches — its own line for a collapsed caret. Committed through the model as ONE
   * undoable update.
   */
  public async applyListToTextSelection(list: TextLineListObject | null, {undoable = true}: UpdateFromObjectOpts = {}): Promise<void> {
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox) {
      return;
    }
    await this.flushPendingTextCommit();
    await this.#ensureBulletsFontForList(list);
    applyListToTextboxSelection(editingTextbox, list, editingTextbox.selectionStart, editingTextbox.selectionEnd);
    await this.updateFromObject({lineStyles: harvestLineStylesFromFabricTextbox(editingTextbox)}, {undoable});
  }

  /**
   * The whole-item form of a list write (the sidebar outside editing): every real line becomes
   * (or stops being) a list row, preserving per-line alignment overrides.
   */
  public buildLineStylesWithListForAllLines(list: TextLineListObject | null): Record<string, TextLineStylesObject> {
    const lineStyles: Record<string, TextLineStylesObject> = {};
    const lineCount = this.text.split('\n').length;
    for (let lineIndex = 0; lineIndex < lineCount; lineIndex++) {
      const existing: TextLineStylesObject | undefined = this.lineStyles[lineIndex];
      const entry: TextLineStylesObject = {
        textAlign: existing?.textAlign ?? null,
        list: list ? withExistingIndent(list, existing?.list ?? null) : null,
      };
      if (entry.textAlign !== null || entry.list !== null) {
        lineStyles[lineIndex] = entry;
      }
    }
    return lineStyles;
  }

  /** Every real line's list state: the uniform list when all lines share one, null otherwise. */
  public getUniformWholeItemList(): TextLineListObject | null {
    return getUniformLineList(this.lineStyles, this.text);
  }

  public hasUnorderedListLine(): boolean {
    return Object.values(this.lineStyles).some((lineStyleObject) => lineStyleObject.list?.type === LIST_TYPES.UNORDERED);
  }

  /** Unordered markers render in the bullets glyph font; load it before the projection measures them. */
  async #ensureBulletsFontForList(list: TextLineListObject | null): Promise<void> {
    if (list?.type !== LIST_TYPES.UNORDERED) {
      return;
    }
    await new Promise<void>((resolve) => {
      loadBulletsFont(BULLETS_FONT_FAMILY, () => {
        resolve();
      });
    });
  }

  /**
   * Slider semantics over a selection: every grapheme's effective size scales by one factor
   * from a baseline captured at drag start, preserving the size contrast within the selection.
   * `targetEffectiveFontSize` is in on-canvas units (font size × item scale), matching the size
   * control's displayed value; the factor is computed against the first grapheme's baseline.
   */
  public async applyFontSizeScaleToTextSelection(targetEffectiveFontSize: number, undoable: boolean): Promise<void> {
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox || editingTextbox.selectionStart >= editingTextbox.selectionEnd) {
      return;
    }
    await this.flushPendingTextCommit();
    const {selectionStart, selectionEnd} = editingTextbox;
    const baseline = this.#getOrCaptureSelectionFontSizeBaseline(editingTextbox, selectionStart, selectionEnd);
    const representativeEffectiveSize = baseline.effectiveFontSizes[0] * this.fabricObject.scaleX;
    const scaleFactor = representativeEffectiveSize > 0 ? targetEffectiveFontSize / representativeEffectiveSize : 1;
    const perGraphemeStyles = baseline.effectiveFontSizes.map((fontSize) => ({
      fontSize: Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, fontSize * scaleFactor)),
    }));

    applyPerGraphemeRunStylesToTextboxSelection(editingTextbox, this.textStyles, selectionStart, perGraphemeStyles);
    await this.updateFromObject({styleRuns: harvestStyleRunsFromFabricTextbox(editingTextbox)}, {undoable});
    if (undoable) {
      this.selectionFontSizeScaleBaseline = null;
    }
  }

  #getOrCaptureSelectionFontSizeBaseline(editingTextbox: Textbox, selectionStart: number, selectionEnd: number): {effectiveFontSizes: number[]} {
    const baseline = this.selectionFontSizeScaleBaseline;
    if (baseline?.selectionStart === selectionStart && baseline?.selectionEnd === selectionEnd) {
      return baseline;
    }
    this.selectionFontSizeScaleBaseline = {
      selectionStart,
      selectionEnd,
      effectiveFontSizes: getEffectiveFontSizesForSelection(editingTextbox, this.textStyles, selectionStart, selectionEnd),
    };
    return this.selectionFontSizeScaleBaseline;
  }

  #publishTextSelectionStyles(): void {
    const editingTextbox = this.getEditingTextbox();
    if (editingTextbox) {
      const summary = getTextSelectionStylesSummary(editingTextbox, this.textStyles, this.uid, this.getSerializedItemShadow());
      this.page.poster.redux.updateTextSelectionStyles(this.pendingTypingStyles.overlayOntoSummary(summary));
    }
  }

  /** The item-level shadow in its serialized form — the base a run's shadow override resolves against. */
  public getSerializedItemShadow(): SerializedShadowOptions | null {
    const itemShadow = this.fabricObject.shadow;
    return itemShadow ? (itemShadow.toObject() as SerializedShadowOptions) : null;
  }

  /**
   * Cmd/Ctrl+B/I/U while editing toggles the selection's styles (the whole item on a collapsed
   * caret). Lives on the session's hidden textarea because the app-level hotkeys layer ignores
   * textarea targets; Cmd/Ctrl+S stays the app-wide save shortcut. Bold follows the panel's
   * rule of being unavailable while the whole-box outline is on.
   */
  protected handleEditModeStyleShortcut(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
      return;
    }
    const key = event.key.toLowerCase();
    const toggledProperty = key === 'b' ? 'isBold' : key === 'i' ? 'isItalic' : key === 'u' ? 'underLine' : null;
    if (toggledProperty === null) {
      return;
    }
    event.preventDefault();
    if (toggledProperty === 'isBold' && this.textStyles.stroke) {
      return;
    }
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox) {
      return;
    }
    // Both branches toggle off the caret/selection's effective value — the state the surfaces
    // display and the style typing would continue with.
    const summary = this.pendingTypingStyles.overlayOntoSummary(getTextSelectionStylesSummary(editingTextbox, this.textStyles, this.uid, this.getSerializedItemShadow()));
    const toggledValue = summary[toggledProperty] !== true;
    const runStylesPartial: Partial<TextRunStylesObject> = {};
    runStylesPartial[toggledProperty] = toggledValue;
    if (editingTextbox.selectionStart < editingTextbox.selectionEnd) {
      void this.applyStylesToTextSelection(runStylesPartial);
    } else {
      this.setPendingTypingStyles(runStylesPartial);
    }
  }

  public updateActiveText(text = ''): void {
    this.replaceWholeTextPreservingRuns(text);
    void this.updateText(text);
  }

  /**
   * Shifts the live textbox's styles for a wholesale text replacement (a plain-textarea surface
   * handing over the full new string) by replacing only the diffed grapheme range through
   * fabric's editing API — runs and per-line records then survive exactly as typed edits would.
   */
  private replaceWholeTextPreservingRuns(text: string): void {
    if (text !== this.text) {
      replaceTextboxTextPreservingStyles(this.fabricTextbox, text);
    }
  }

  /**
   * Updates the text while growing the text box so the content never soft-wraps: line breaks
   * happen only where the user typed a newline. Used by the mobile text panel's textarea, which
   * (unlike on-canvas editing) should keep each typed line on a single line. The box only ever
   * grows — it is never shrunk below its current width, so wrapping is prevented without the box
   * collapsing when text is removed.
   */
  public async updateTextWithoutWrapping(textItemObject: DeepPartial<TextItemObject>, opts: UpdateFromObjectOpts = {}): Promise<void> {
    const isReplacingText = textItemObject.text !== undefined && textItemObject.text !== this.text;
    if (isReplacingText && textItemObject.text !== undefined) {
      this.replaceWholeTextPreservingRuns(textItemObject.text);
    }
    await this.updateFromObject(
      {
        ...(isReplacingText ? {styleRuns: harvestStyleRunsFromFabricTextbox(this.fabricTextbox), lineStyles: harvestLineStylesFromFabricTextbox(this.fabricTextbox)} : {}),
        ...textItemObject,
        baseWidth: this.getGrownBaseWidthForText(textItemObject.text ?? this.text),
      },
      opts
    );
  }

  /**
   * Returns the base width needed to keep `text` on a single line per hard newline, never below the
   * current width (grow-only, so the box never collapses when text is removed). This is the fit used
   * by the text panel / slide HTML editors so typing never soft-wraps a word.
   */
  public getGrownBaseWidthForText(text: string): number {
    return Math.max(this.baseWidth, this.getWidthToFitUnwrappedText(text));
  }

  /**
   * Measures the width the text box needs so the given text renders without any soft-wrapping,
   * i.e. the width of the widest hard line (split only on explicit newlines). The live fabric
   * textbox is measured with wrapping effectively disabled, then its state is restored.
   */
  private getWidthToFitUnwrappedText(text: string): number {
    const textbox = this.fabricTextbox;
    const originalText = textbox.text;
    const originalWidth = textbox.width;
    textbox.set({text, width: UNWRAPPED_TEXT_MEASURE_WIDTH});
    textbox.initDimensions();
    const fittedWidth = textbox.calcTextWidth() + this.textStyles.fontSize * UNWRAPPED_TEXT_WIDTH_BUFFER_FACTOR;
    textbox.set({text: originalText, width: originalWidth});
    textbox.initDimensions();
    return fittedWidth;
  }

  public getUpdatedModelCoords(textChildCenterPoint: FabricPoint, groupBottomLeft: Point): ModelCoordinates {
    switch (this.verticalAlign) {
      case TextVerticalAlignType.TOP:
        return this.getCoordsForTopVerticalAlign();

      case TextVerticalAlignType.CENTER:
        return this.getCoordsForCenterVerticalAlign(textChildCenterPoint);

      case TextVerticalAlignType.BOTTOM:
        return this.getCoordsForBottomVerticalAlign(groupBottomLeft);

      default:
        throw new Error(`Invalid text align type`);
    }
  }

  /**
   * Returns the coordinates to be used to update model when the text is vertically top aligned.
   */
  protected getCoordsForTopVerticalAlign(): ModelCoordinates {
    const parentViewComponent = this.hasParentGroupGraphicItem() ? this.fabricObject.group : this.fabricObject;

    if (parentViewComponent) {
      const centerPoint = parentViewComponent.getCenterPoint();
      const theta = util.degreesToRadians(parentViewComponent.angle ?? 0);

      return {
        x:
          centerPoint.x -
          ((this.fabricObject.width * parentViewComponent.scaleX) / 2) * Math.cos(theta) +
          ((this.fabricObject.height * parentViewComponent.scaleY) / 2) * Math.sin(theta),
        y:
          centerPoint.y -
          ((this.fabricObject.width * parentViewComponent.scaleY) / 2) * Math.sin(theta) -
          ((this.fabricObject.height * parentViewComponent.scaleY) / 2) * Math.cos(theta),
      };
    }
    return {};
  }

  /**
   * Returns the coordinates to be used to update model when the text is vertically center aligned.
   */
  protected getCoordsForCenterVerticalAlign(textChildCenterPoint: FabricPoint): Point {
    const parentViewComponent = this.hasParentGroupGraphicItem() ? (this.fabricObject.group ?? this.fabricObject) : this.fabricObject;
    const vcWidth = this.fabricObject.width;
    const theta = util.degreesToRadians(parentViewComponent.angle);

    return {
      x:
        textChildCenterPoint.x -
        ((vcWidth * parentViewComponent.scaleX) / 2) * Math.cos(theta) +
        ((this.fabricObject.height * parentViewComponent.scaleY) / 2) * Math.sin(theta),
      y:
        textChildCenterPoint.y -
        ((vcWidth * parentViewComponent.scaleY) / 2) * Math.sin(theta) -
        ((this.fabricObject.height * parentViewComponent.scaleY) / 2) * Math.cos(theta),
    };
  }

  /**
   * Returns the coordinates to be used to update model when the text is vertically bottom aligned.
   * @param {Object} blCoords. The bottom left coordinate of group fabricObject before it's dimensions were updated.
   * @return {Object}
   * @private
   */
  protected getCoordsForBottomVerticalAlign(blCoords: Point): Point {
    const parentViewComponent = this.hasParentGroupGraphicItem() ? (this.fabricObject.group ?? this.fabricObject) : this.fabricObject;
    const theta = degreesToRadians(parentViewComponent.angle);

    return {
      x: blCoords.x + this.fabricObject.height * parentViewComponent.scaleX * Math.sin(theta),
      y: blCoords.y - this.fabricObject.height * parentViewComponent.scaleY * Math.cos(theta),
    };
  }

  protected updateGroupDimensions(textChildDimensions: FabricItemDimensions, newVerticalPadding: number): void {
    this.fabricObject.set({
      height: textChildDimensions.height + this.padding * 2 + newVerticalPadding,
      width: textChildDimensions.width + this.padding * 2,
    });
  }

  public override doesfabricObjBelongtoItem(fabricObj: FabricObject | Group): boolean {
    return this.fabricObject === fabricObj || this.fabricTextbox === fabricObj;
  }

  protected onTextEditModeEntered(): void {
    const hiddenTextarea = this.fabricTextbox.hiddenTextarea;
    if (hiddenTextarea) {
      hiddenTextarea.setAttribute('maxlength', MAX_CHARACTERS_LIMIT);
      this.page.spellCheck.checkSpell(this.fabricTextbox, true).catch(noop);
    }
  }

  protected onEditingExited(): void {
    // Commit any debounced typing while the shifted styles map is still current; the values are
    // captured synchronously, so the teardown below is safe to continue.
    void this.flushPendingTextCommit();
    for (const disposeSessionListener of this.editSessionListenerDisposers) {
      disposeSessionListener();
    }
    this.editSessionListenerDisposers = [];
    // Fabric already removed the session's hidden textarea (and with it these listeners) from
    // the DOM before firing editing:exited; only the references need clearing.
    this.hiddenTextareaPasteListener = null;
    this.hiddenTextareaInputListener = null;
    this.hiddenTextareaKeydownListener = null;
    this.hiddenTextareaBeforeInputListener = null;
    this.pendingTypingStyles.discard();
    this.fabricObject.set({subTargetCheck: false, interactive: false});
    this.fabricTextbox.set({perPixelTargetFind: true});
    this.stopKeepingGroupCacheFreshAfterEditSession();
    this.editSessionBaseWidth = null;
    this.selectionFontSizeScaleBaseline = null;
    this.page.poster.redux.clearTextSelectionStyles();
    this.page.spellCheck.closeSuggestionPopUp();
    closeTextSelectionPopup();
    this.page.canvasPanOnTextEdit.afterTextEditExited();

    unhideCustomControls(this.page.hashedID);
  }

  public restoreDefaultState(): void {
    if (this.getEditingTextbox()) {
      this.fabricTextbox.exitEditing();
      this.page.activeSelection.setActiveObject(this.fabricObject as unknown as FabricObject);
    }
  }

  /**
   * Gets the canvas-space position of a text-child-shaped object (e.g. an animation clip path)
   * according to the item's vertical alignment.
   */
  public getTextChildAbsolutePosition(textChildViewComponent: FabricObject): ClonePositionalParams {
    const parentViewComponent = this.hasParentGroupGraphicItem() ? (this.fabricObject.group ?? this.fabricObject) : this.fabricObject;
    const cornerPoints = textChildViewComponent.getCornerPoints(parentViewComponent.getCenterPoint());
    let verticalParams: ClonePositionalParams;

    if (parentViewComponent) {
      parentViewComponent.setCoords();
    }

    switch (this.verticalAlign) {
      case TextVerticalAlignType.TOP:
        verticalParams = this.#getPaddingInsetPositionFromCorner(parentViewComponent.aCoords.tl, parentViewComponent, 1);
        break;

      case TextVerticalAlignType.CENTER:
        verticalParams = {
          top: (cornerPoints.tl.y + cornerPoints.bl.y) / 2,
          left: (cornerPoints.tl.x + cornerPoints.bl.x) / 2,
        };
        break;

      case TextVerticalAlignType.BOTTOM:
        verticalParams = this.#getPaddingInsetPositionFromCorner(parentViewComponent.aCoords.bl, parentViewComponent, -1);
        break;

      default:
        throw new Error(`Invalid text align type`);
    }

    verticalParams.originY = this.verticalAlign;
    return verticalParams;
  }

  /**
   * Canvas-space position of the text child inset by the item padding from a group corner: the
   * inset vector (padding·scaleX, verticalDirection·padding·scaleY) rotated by the group's angle.
   */
  #getPaddingInsetPositionFromCorner(corner: FabricPoint, parentViewComponent: FabricObject | Group, verticalDirection: 1 | -1): ClonePositionalParams {
    const theta = degreesToRadians(parentViewComponent.angle);
    const insetX = this.padding * parentViewComponent.scaleX;
    const insetY = verticalDirection * this.padding * parentViewComponent.scaleY;

    return {
      left: corner.x + insetX * Math.cos(theta) - insetY * Math.sin(theta),
      top: corner.y + insetX * Math.sin(theta) + insetY * Math.cos(theta),
    };
  }

  public getPaddedTextChildAbsolutePosition(textChildViewComponent: FabricObject): ClonePositionalParams {
    const parentViewComponent = this.hasParentGroupGraphicItem() ? (this.fabricObject.group ?? this.fabricObject) : this.fabricObject;
    const cornerPoints = textChildViewComponent.getCornerPoints(parentViewComponent.getCenterPoint());
    let verticalParams: ClonePositionalParams;

    if (parentViewComponent) {
      parentViewComponent.setCoords();
    }

    switch (this.verticalAlign) {
      case TextVerticalAlignType.TOP:
        verticalParams = {
          top: parentViewComponent.aCoords.tl.y,
          left: parentViewComponent.aCoords.tl.x,
        };
        break;

      case TextVerticalAlignType.CENTER:
        verticalParams = {
          top: (cornerPoints.tl.y + cornerPoints.bl.y) / 2,
          left: (cornerPoints.tl.x + cornerPoints.bl.x) / 2,
        };
        break;

      case TextVerticalAlignType.BOTTOM:
        verticalParams = {
          top: parentViewComponent.aCoords.bl.y,
          left: parentViewComponent.aCoords.bl.x,
        };
        break;

      default:
        throw new Error(`Invalid text align type`);
    }

    verticalParams.originY = this.verticalAlign;
    return verticalParams;
  }

  public getMinWidth(): number {
    return this.fabricTextbox.getMinWidth() + this.fabricTextbox.strokeWidth + this.padding * 2;
  }

  public onModifyWidth(): void {
    this.updateTextChildWidth();
    this.updateFabricGroupHeight();
    this.setTextChildPosition();
    this.setBackgroundParams();
  }

  public updateTextChildWidth(): void {
    this.fabricTextbox.set({
      width: this.calculateBaseWidth(),
    });
  }

  public calculateBaseWidth(): number {
    return this.fabricObject.width - this.padding * 2 - this.getStrokeWidth();
  }

  protected calculateGroupWidth(): number {
    return this.getTextStrokedWidth() + this.padding * 2;
  }

  protected calculateGroupHeight(): number {
    return this.getTextStrokedHeight() + this.padding * 2 + this.verticalPadding;
  }

  /**
   * The stroke the laid-out textbox carries, which the group width adds and the base width
   * subtracts; both must use the same figure or every write-back of the measurements (a resize)
   * grows the box by the difference. Before the textbox exists the stroke is derived the way
   * {@link applyTextStroke} will apply it: an enabled outline wins over the faux-bold stroke.
   */
  protected getStrokeWidth(): number {
    if (this.fabricTextbox) {
      return this.fabricTextbox.strokeWidth;
    }
    if (this.textStyles.stroke) {
      return this.getOutlineStrokeWidth();
    }
    if (this.textStyles.isBold && !isBoldVariationAvaliableForFont(this.textStyles.fontFamily)) {
      return getBoldStrokeWidth(this);
    }
    return DEFAULT_STROKE_WIDTH;
  }

  protected getOutlineStrokeWidth(): number {
    return TEXT_OUTLINE_STROKE_WIDTH_FACTOR * this.textStyles.fontSize * this.textStyles.strokeWidth;
  }

  public override getManualSelectorPadding(): number {
    return this.padding * this.fabricObject.scaleX;
  }

  protected updateFabricGroupHeight(): void {
    this.fabricObject.height = this.calculateGroupHeight();
  }

  protected updateFabricGroupWidth(): void {
    this.fabricObject.width = this.calculateGroupWidth();
  }

  protected getTextStrokedWidth(): number {
    return this.fabricTextbox.width + this.fabricTextbox.strokeWidth;
  }

  public getTextStrokedHeight(): number {
    return this.fabricTextbox.height + this.fabricTextbox.strokeWidth;
  }

  public setTextChildPosition(): void {
    this.setTextTopPositionByVerticalAlignment();
    this.setTextLeftPosition();
  }

  protected setTextTopPositionByVerticalAlignment(): void {
    switch (this.verticalAlign) {
      case TextVerticalAlignType.TOP:
        this.fabricTextbox.set({
          top: -this.fabricObject.height / 2 + this.padding,
        });
        break;

      case TextVerticalAlignType.CENTER:
        this.fabricTextbox.set({
          top: -this.getTextStrokedHeight() / 2,
        });
        break;

      case TextVerticalAlignType.BOTTOM:
        this.fabricTextbox.set({
          top: this.fabricObject.height / 2 - this.padding - this.getTextStrokedHeight(),
        });
        break;

      default:
        throw new Error(`Invalid text align type`);
    }
  }

  protected setTextLeftPosition(): void {
    this.fabricTextbox.set({
      left: -this.fabricObject.width / 2 + this.padding,
    });
  }

  public getMinHeight(): number {
    return this.getTextStrokedHeight() + this.padding * 2;
  }

  public onModifyHeight(): void {
    this.setTextChildPosition();
    this.setBackgroundParams();
  }

  public getVerticalPadding(): number {
    return this.fabricObject.height - this.getTextStrokedHeight() - this.padding * 2;
  }

  public getFill(): string | TFiller | null {
    return this.fabricTextbox.fill;
  }

  public override hasEditMode(): boolean {
    return true;
  }

  public override getColors(): RGBA[] {
    let colors: RGBA[] = super.getColors();

    if (this.textStyles.fill.hasFill()) {
      colors = [...colors, ...this.textStyles.fill.fillColor];
    }

    if (this.hasBackground()) {
      colors = [...colors, ...this.background.fill.fillColor];
    }

    for (const lineStyleObject of Object.values(this.lineStyles)) {
      if (lineStyleObject.list) {
        if (lineStyleObject.list.fill !== null) {
          colors.push(lineStyleObject.list.fill);
        }
      }
    }

    return [...colors, ...this.styleRuns.getColors()];
  }
}

export const addTextToPoster = (coordinates?: ItemCoordinates): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  void currentPage.items.addItems.addTextItem({
    type: ElementDataType.TEXT,
    ...coordinates,
  });
};


/**
 * FONTSIZE_FRACTION is a hash map where the key is the font family, this is used to assign a value to _fontSizeFraction
 * when the poster made in flash is opened in HTML5 builder.
 */
export const FONTSIZE_FRACTION = {
  AccidentalPresidency: 0.18,
  Acknowledgement: 0.3,
  AdventProRegular: 0.15,
  AgentOrange: 0.285,
  AlexBrushRegular: 0.285,
  AnimeAce: 0.27,
  AnuDaw: 0.235,
  ArbuckleRemix: 0.38,
  ArchivoBlackRegular: 0.39,
  AsapRegular: 0.18,
  AYummyApology: 0.39,
  Bangers: 0.22,
  BerkshireSwashRegular: 0.12,
  BigApple: 0.31,
  Blackout2am: 0.35,
  BlackoutMidnight: 0.28,
  BlackRose: 0.21,
  BostonTraffic: 0.29,
  BowlbyOneSCRegular: 0.04,
  BubbleBoy2: 0,
  BurnstownDam: 0.42,
  CACChampagne: 0.34,
  Caeldera: 0.39,
  Chancery: 0,
  Chopin: 0,
  Chumbly: 0.45,
  Coda: 0.28,
  ComingSoon: 0.35,
  Concielian: 0.42,
  DayPosterBlack: 0.02,
  DigitalDream: 0.34,
  Effloresce: 0.29,
  EraserRegular: 0.15,
  FingerPaintRegular: 0.02,
  FrijoleRegular: 0.1,
  FugazOneRegular: 0.08,
  GermaniaOneRegular: 0.22,
  HamburgerHeaven: 0.18,
  HennyPennyRegular: 0.0,
  HoltwoodOneSC: -0.08,
  JockeyOneRegular: 0.05,
  JosefinSlabRegular: 0.39,
  KaushanScriptRegular: 0.03,
  LeagueSpartanBold: 0.38,
  LemonRegular: 0.09,
  LiberationSerif: 0.42,
  Liquidism: 0.34,
  LoveYaLikeASister: 0.2,
  MerriweatherSansRegular: 0.15,
  MetalLord: 0.38,
  MetalManiaRegular: 0.19,
  Monoton: -0.05,
  MountainsofChristmas: 0.2,
  MouseMemoirsRegular: 0.18,
  NixieOneRegular: 0.18,
  Oklahoma: 0.1,
  OldStandardRegular: 0.36,
  OpenSansRegular: 0.36,
  OperatingInstructions: 0.36,
  OriginalSurferRegular: 0.14,
  PaprikaRegular: -0.03,
  PompiereRegular: 0.19,
  Porter: 0.38,
  Primitive: 0.2,
  SacramentoRegular: 0.2,
  SancreekRegular: 0.09,
  SevenMonkeyFury: 0.23,
  SFCollegiate: 0.34,
  SFSpeedwaystar: 0.45,
  ShadowsIntoLightTwoRegular: 0.02,
  Shermlock: 0.065,
  SirinStencilRegular: -0.05,
  SixCaps: 0.025,
  SmokumRegular: 0.14,
  SoftSugar: 0.325,
  Solov2: 0.42,
  SpecialElite: 0.42,
  SpiraxRegular: 0.19,
  Sveningsson: 0.19,
  TagsXtreme: 0.36,
  Titania: 0.22,
  TulpenOneRegular: 0.25,
  Ultra: 0.12,
  WaitingfortheSunrise: 0.0,
  Windsong: 0.32,
};

/**
 * A hash map that contains the expansion factor for text's canvas cache. It only contains factor for fonts which get cut off with fabric's default behavior (default text's canvas cache dimensions).
 */
export const CUSTOM_CACHE_EXPANSION_FACTOR = {
  AmsterdamOne: 2.6,
};
